import {Controller} from "@hotwired/stimulus"

// How it works:
//   1. Detects @query while typing → shows autocomplete dropdown
//   2. On select → inserts "@display_name " (clean) in textarea; stores display_name→id in _resolved
//   3. On submit → replaces every resolved "@display_name" with "@[display_name:id]" before the form is serialised
//   4. On reset (after turbo submit) → clears state; textarea is already cleared by rich-form#resetForm
export default class extends Controller {
    static targets = ["input", "dropdown"]
    static values = {url: String}

    connect() {
        this._resolved = new Map()   // display_name → id for confirmed mentions
        this._users = []
        this._atPos = null
        this._query = null
        this._selectedIndex = -1
        this._debounceTimer = null
        this._boundClickOutside = this._clickOutside.bind(this)
        document.addEventListener("click", this._boundClickOutside)
    }

    disconnect() {
        document.removeEventListener("click", this._boundClickOutside)
        clearTimeout(this._debounceTimer)
    }

    // ===== actions =====

    handleInput() {
        const match = this._getMentionQuery()
        if (!match) {
            this._closeDropdown();
            return
        }
        this._atPos = match.atPos
        this._query = match.query
        clearTimeout(this._debounceTimer)
        this._debounceTimer = setTimeout(() => this._fetchUsers(match.query), 200)
    }

    handleKeydown(event) {
        if (!this._isOpen()) return
        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                this._navigate(1);
                break
            case "ArrowUp":
                event.preventDefault();
                this._navigate(-1);
                break
            case "Enter":
            case "Tab":
                if (this._selectedIndex >= 0) {
                    event.preventDefault()
                    this._selectUser(this._users[this._selectedIndex])
                }
                break
            case "Escape":
                event.preventDefault();
                this._closeDropdown();
                break
        }
    }

    selectUser(event) {
        const index = parseInt(event.currentTarget.dataset.mentionIndex, 10)
        this._selectUser(this._users[index])
    }

    // Called on submit — tokenises resolved @names before the form is serialised by Turbo
    handleSubmit() {
        if (this._resolved.size === 0) return
        const input = this.inputTarget
        let value = input.value

        // Replace longest names first to avoid partial-name collisions (e.g. "John" before "John Smith")
        const sorted = [...this._resolved.entries()].sort(([a], [b]) => b.length - a.length)
        sorted.forEach(([name, id]) => {
            const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
            // Match @name at word boundary: followed by space, punctuation, another @ or end of string
            value = value.replace(
                new RegExp(`@${escaped}(?=[\\s,;.!?@\\n]|$)`, "g"),
                `@[${name}:${id}]`
            )
        })

        input.value = value
    }

    // Called on turbo:submit-end — clears state (textarea is cleared by rich-form#resetForm)
    reset() {
        this._closeDropdown()
        this._resolved.clear()
    }

    // ===== private =====

    _getMentionQuery() {
        const input = this.inputTarget
        const pos = input.selectionStart
        const text = input.value.substring(0, pos)
        // @ preceded by start or whitespace; query must end with a non-space char so that
        // inserting "@Name " (trailing space) doesn't immediately re-trigger the dropdown
        const match = text.match(/(^|[\s\n])@([^@\n\]]*\S)$/)
        if (!match) return null
        const query = match[2]
        const atPos = pos - query.length - 1
        return {query, atPos}
    }

    async _fetchUsers(query) {
        if (query.length === 0) {
            this._closeDropdown();
            return
        }
        try {
            const resp = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
                headers: {Accept: "application/json", "X-Requested-With": "XMLHttpRequest"}
            })
            if (!resp.ok) return
            this._users = await resp.json()
            this._renderDropdown(this._users)
        } catch {
            this._closeDropdown()
        }
    }

    _renderDropdown(users) {
        const dropdown = this.dropdownTarget
        dropdown.innerHTML = ""
        if (users.length === 0) {
            this._closeDropdown();
            return
        }

        users.forEach((user, index) => {
            const btn = document.createElement("button")
            btn.type = "button"
            btn.className = "w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
            btn.dataset.mentionIndex = index
            btn.dataset.action = "click->mention#selectUser"

            const avatar = document.createElement("span")
            avatar.className = "shrink-0 flex items-center justify-center size-7 rounded-full bg-sky-100 dark:bg-sky-900 text-sky-600 dark:text-sky-300 text-xs font-semibold select-none"
            avatar.textContent = user.display_name.charAt(0).toUpperCase()

            const name = document.createElement("span")
            name.className = "min-w-0 truncate text-zinc-800 dark:text-zinc-100 font-medium"
            name.textContent = `@${user.display_name}`

            btn.append(avatar, name)
            dropdown.appendChild(btn)
        })

        this._selectedIndex = -1
        dropdown.classList.remove("hidden")
    }

    _navigate(direction) {
        const items = this.dropdownTarget.querySelectorAll("button")
        if (items.length === 0) return
        const prev = this._selectedIndex
        this._selectedIndex = Math.max(0, Math.min(items.length - 1, this._selectedIndex + direction))
        if (prev >= 0 && prev < items.length) items[prev].classList.remove("bg-zinc-100", "dark:bg-zinc-700")
        items[this._selectedIndex].classList.add("bg-zinc-100", "dark:bg-zinc-700")
    }

    _selectUser(user) {
        if (!user) return
        const input = this.inputTarget
        const current = this._getMentionQuery() || {atPos: this._atPos, query: this._query || ""}
        const before = input.value.substring(0, current.atPos)
        const after = input.value.substring(current.atPos + 1 + current.query.length)

        // Insert clean @name (no token) — tokenisation happens in handleSubmit
        const display = `@${user.display_name}`
        input.value = `${before}${display} ${after}`
        input.selectionStart = input.selectionEnd = before.length + display.length + 1

        this._resolved.set(user.display_name, user.id)

        input.focus()
        input.dispatchEvent(new Event("input", {bubbles: true}))
        this._closeDropdown()
    }

    _closeDropdown() {
        if (this.hasDropdownTarget) {
            this.dropdownTarget.classList.add("hidden")
            this.dropdownTarget.innerHTML = ""
        }
        this._selectedIndex = -1
        this._users = []
    }

    _isOpen() {
        return this.hasDropdownTarget && !this.dropdownTarget.classList.contains("hidden")
    }

    _clickOutside(event) {
        if (!this.element.contains(event.target)) this._closeDropdown()
    }
}
